import React, { useMemo, useState } from 'react';
import {
  AppBar,
  Avatar,
  Box,
  Button,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  AssignmentOutlined,
  CampaignOutlined,
  EventAvailableOutlined,
  FactCheckOutlined,
  FeedOutlined,
  HomeOutlined,
  InsightsOutlined,
  LocalDiningOutlined,
  LogoutOutlined,
  MenuOutlined,
  ScheduleOutlined,
  SchoolOutlined,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import WhatsNewDialog from '../../components/WhatsNewDialog';

const drawerWidth = 292;
const mobileDrawerWidth = 280;
const desktopTopBarHeight = 80;
const mobileTopBarHeight = 72;

const navItems = [
  { label: 'Dashboard', path: '/teacher-portal', icon: HomeOutlined, exact: true },
  { label: 'Clock In / Out', path: '/teacher-portal/clock', icon: EventAvailableOutlined },
  { label: 'Student Attendance', path: '/teacher-portal/student-attendance', icon: FactCheckOutlined },
  { label: 'Exams and Report', path: '/teacher-portal/exams-report', icon: InsightsOutlined },
  { label: 'Feeding Fee', path: '/teacher-portal/feeding-fee', icon: LocalDiningOutlined },
  { label: 'Assignments', path: '/teacher-portal/assignments', icon: AssignmentOutlined },
  { label: 'Announcements', path: '/teacher-portal/announcements', icon: CampaignOutlined },
  { label: 'Timetable', path: '/teacher-portal/timetable', icon: ScheduleOutlined },
  { label: 'Weekly Exercises', path: '/teacher-portal/exercises', icon: FeedOutlined },
];

const getInitials = (name = '') =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('') || 'T';

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  borderBottom: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.55 : 0.7)}`,
  boxShadow: 'none',
  backgroundImage: 'none',
}));

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  width: drawerWidth,
  flexShrink: 0,
  '& .MuiDrawer-paper': {
    width: drawerWidth,
    boxSizing: 'border-box',
    backgroundColor: theme.palette.background.paper,
    borderRight: `1px solid ${theme.palette.divider}`,
    boxShadow: 'none',
    backgroundImage: 'none',
    [theme.breakpoints.down('md')]: {
      width: mobileDrawerWidth,
    },
  },
}));

const MainContent = styled('main')(({ theme }) => ({
  flexGrow: 1,
  minHeight: '100dvh',
  width: `calc(100% - ${drawerWidth}px)`,
  padding: theme.spacing(3),
  paddingTop: theme.spacing(3),
  backgroundColor: theme.palette.background.default,
  overflowX: 'hidden',
  [theme.breakpoints.down('md')]: {
    width: '100%',
    padding: theme.spacing(2),
    '& .MuiGrid-item': {
      flexBasis: '100% !important',
      maxWidth: '100% !important',
    },
    '& .MuiCard-root': {
      width: '100%',
      maxWidth: '100%',
      minWidth: 0,
      boxSizing: 'border-box',
    },
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5),
  },
  '& .MuiTypography-h4': {
    fontSize: '1.55rem',
    letterSpacing: 0,
  },
  '& .MuiTypography-h6': {
    letterSpacing: 0,
  },
  '& .MuiCard-root': {
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: theme.shadows[3],
    backgroundImage: 'none',
    backgroundColor: theme.palette.background.paper,
    [theme.breakpoints.down('sm')]: {
      boxShadow: theme.shadows[1],
    },
  },
  '& .MuiCard-root:hover': {
    boxShadow: theme.shadows[4],
    [theme.breakpoints.down('sm')]: {
      boxShadow: theme.shadows[1],
    },
  },
  '& .MuiCardContent-root': {
    padding: theme.spacing(2.25),
  },
  '& .MuiButton-root': {
    borderRadius: 8,
    textTransform: 'none',
    boxShadow: 'none',
  },
  '& .MuiButton-contained': {
    boxShadow: 'none',
  },
  '& .MuiOutlinedInput-root': {
    borderRadius: 8,
    backgroundColor: theme.palette.background.paper,
  },
  '& .MuiChip-root': {
    borderRadius: 8,
    fontWeight: 700,
  },
  '& .MuiAlert-root': {
    borderRadius: 8,
  },
  '& .MuiTableHead-root .MuiTableCell-root': {
    backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.14 : 0.06),
    color: theme.palette.text.primary,
    fontWeight: 800,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  '& .MuiTableCell-root': {
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.75)}`,
  },
}));

export default function TeacherPortalLayout() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const teacher = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}');
    } catch {
      return {};
    }
  }, []);

  const schoolName = teacher?.school?.name || teacher?.schoolName || 'School';
  const profileImage =
    teacher?.profilePicture || teacher?.profileImage || teacher?.user?.profilePicture;

  const activeTitle =
    navItems.find((item) =>
      item.exact ? location.pathname === item.path : location.pathname.startsWith(item.path)
    )?.label || 'Teacher Portal';

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    navigate('/teacher-portal/login', { replace: true });
  };

  const drawerContent = (
    <Stack sx={{ height: '100%' }}>
      <Box sx={{ p: 2.5 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 1,
              display: 'grid',
              placeItems: 'center',
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: 'primary.main',
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            }}
          >
            <SchoolOutlined />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={800} noWrap>
              Teacher Portal
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {schoolName}
            </Typography>
          </Box>
        </Stack>
      </Box>

      <Divider />

      <List sx={{ p: 1.5, flex: 1 }}>
        {navItems.map((item) => {
          const Icon = item.icon;
          const selected = item.exact
            ? location.pathname === item.path
            : location.pathname.startsWith(item.path);

          return (
            <ListItemButton
              key={item.path}
              selected={selected}
              onClick={() => {
                navigate(item.path);
                setMobileOpen(false);
              }}
              sx={{
                minHeight: 48,
                mb: 0.5,
                borderRadius: 1,
                border: `1px solid ${selected ? alpha(theme.palette.primary.main, 0.35) : 'transparent'}`,
                bgcolor: selected ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                color: selected ? 'primary.main' : 'text.primary',
                '&.Mui-selected': {
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                },
                '&.Mui-selected:hover, &:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.07),
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 38, color: 'inherit' }}>
                <Icon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  fontSize: '0.9rem',
                  fontWeight: selected ? 800 : 600,
                }}
              />
            </ListItemButton>
          );
        })}
      </List>

      <Divider />

      <Box sx={{ p: 1.5 }}>
        <Button
          fullWidth
          color="inherit"
          onClick={handleLogout}
          startIcon={<LogoutOutlined />}
          sx={{ justifyContent: 'flex-start' }}
        >
          Logout
        </Button>
      </Box>
    </Stack>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100dvh', bgcolor: 'background.default' }}>
      <CssBaseline />

      <StyledAppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
        }}
      >
        <Toolbar sx={{ minHeight: { xs: mobileTopBarHeight, sm: desktopTopBarHeight }, gap: 2 }}>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => setMobileOpen(true)}
            sx={{ display: { md: 'none' } }}
            aria-label="Open navigation"
          >
            <MenuOutlined />
          </IconButton>

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="h6" fontWeight={800} noWrap>
              {activeTitle}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {schoolName}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1.5} alignItems="center">
            <Tooltip title={teacher?.name || 'Teacher'}>
              <Avatar src={profileImage || undefined} sx={{ width: 38, height: 38 }}>
                {getInitials(teacher?.name)}
              </Avatar>
            </Tooltip>
            <Box sx={{ display: { xs: 'none', sm: 'block' }, minWidth: 0 }}>
              <Typography variant="body2" fontWeight={700} noWrap>
                {teacher?.name || 'Teacher'}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {teacher?.email || ''}
              </Typography>
            </Box>
          </Stack>
        </Toolbar>
      </StyledAppBar>

      <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
        <StyledDrawer
          variant={isMobile ? 'temporary' : 'permanent'}
          open={isMobile ? mobileOpen : true}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
        >
          {drawerContent}
        </StyledDrawer>
      </Box>

      <MainContent className="teacher-portal-admin">
        <Toolbar sx={{ minHeight: { xs: mobileTopBarHeight, sm: desktopTopBarHeight } }} />
        <Outlet />
        <WhatsNewDialog platform="teacher-portal" />
      </MainContent>
    </Box>
  );
}
